import { ChevronRightIcon } from 'lucide-react'
import type * as React from 'react'
import { createContext, useContext } from 'react'

import { cn } from '../../lib/utils'

type BreadcrumbLinkRenderer = (
  props: React.ComponentProps<'a'> & { href: string },
) => React.ReactElement

const BreadcrumbLinkContext = createContext<BreadcrumbLinkRenderer | null>(null)

function BreadcrumbLinkProvider({
  renderLink,
  children,
}: {
  renderLink: BreadcrumbLinkRenderer
  children: React.ReactNode
}) {
  return (
    <BreadcrumbLinkContext.Provider value={renderLink}>{children}</BreadcrumbLinkContext.Provider>
  )
}

/**
 * The trail above a page title (DESIGN.md "Navigation": breadcrumbs on every
 * page; the first crumb returns to the module home).
 *
 * An inline list at 13px in `--muted-foreground`, wrapping on a phone, 8px
 * above the title. The last crumb is the only one in full foreground: the
 * trail reads as "where you are", the ancestors deliberately quieter.
 */
function Breadcrumb({ className, children, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      data-slot="breadcrumb"
      className={cn('text-muted-foreground min-w-0 shrink-0 text-sm', className)}
      {...props}
    >
      <ol className="flex flex-wrap items-center gap-1">{children}</ol>
    </nav>
  )
}

/**
 * `current` marks the trailing crumb. It also sets `aria-current="page"`, which
 * is what a screen reader uses to announce the end of the trail — the colour
 * change alone would say nothing.
 */
function BreadcrumbItem({
  className,
  current,
  children,
  ...props
}: React.ComponentProps<'li'> & { current?: boolean }) {
  return (
    <li
      data-slot="breadcrumb-item"
      aria-current={current ? 'page' : undefined}
      className={cn('inline-flex min-w-0 items-center', current && 'text-foreground', className)}
      {...props}
    >
      {children}
    </li>
  )
}

/**
 * An ancestor crumb link. Underlined at rest, because with ink as the accent
 * colour alone cannot tell a link from text ("The Shape-as-Well-as-Colour
 * Rule"); the rest underline is a quiet hairline that thickens on hover.
 */
function BreadcrumbLink({ className, ...props }: React.ComponentProps<'a'>) {
  const renderLink = useContext(BreadcrumbLinkContext)
  const linkProps = {
    'data-slot': 'breadcrumb-link',
    ...props,
    className: cn(
      'text-muted-foreground decoration-border underline decoration-1 underline-offset-[3px] transition-ui hover:text-foreground hover:decoration-current hover:decoration-2',
      className,
    ),
  }

  return renderLink && props.href ? (
    renderLink({ ...linkProps, href: props.href })
  ) : (
    <a {...linkProps} />
  )
}

/**
 * Decorative. The chevron is a physical direction, so it mirrors under RTL:
 * `rtl:rotate-180` rather than a second glyph.
 */
function BreadcrumbSeparator({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      aria-hidden="true"
      className={cn('text-muted-foreground inline-flex items-center', className)}
      {...props}
    >
      <ChevronRightIcon className="size-3.5 rtl:rotate-180" />
    </li>
  )
}

export { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbLinkProvider, BreadcrumbSeparator }
